/**
 * HostObjectTools: 主持人在联机房间放置 / 移动动态 GLB 物体的轻量工具会话。
 *
 * 复用离线编辑器的放置与 Gizmo 子系统（EditorPlacement / EditorGizmo），但
 * 不带 SceneEditor 的选区 / 属性面板 / 历史栈 / 保存——多人场景下离线编辑器
 * 被禁用（enterEditor 守卫），动态物体走服务端权威同步通道：
 *   放置：光标射线吸附表面（或滚轮调距）预览 → 左键确认 → onSpawn 上行，
 *         服务端广播 room:objects{spawn}，本人也经事件路径应用。
 *   编辑：editObject(id) 直接把 Gizmo 附着到该物体 → W 移动 / E 旋转切换 →
 *         拖拽结束 onMove 上行完整变换（服务端广播，其他客户端 setTransform
 *         平滑应用，不重建 marker）。拖拽过程仅本地预览，松手才同步。
 *
 * 会话激活期间释放指针锁、光标驱动：main.ts 需在 click / wheel / keydown
 * 入口读 active 跳过常规交互，主循环每帧调 update()；右键拖动经 onLook
 * 转视角供瞄准。Esc 取消放置 / 结束编辑。
 */

import { Vec3 } from 'playcanvas';
import type { Application, Entity } from 'playcanvas';
import type { Collision } from '../collision';
import type { SceneObjectManager } from '../scene/SceneObjectManager';
import { EditorPlacement } from '../editor/EditorPlacement';
import { EditorGizmo } from '../editor/EditorGizmo';
import { EditorState } from '../editor/EditorState';
import type { EditorAsset } from '../editor/EditorAssetLibrary';

/** 模型库条目（GET /hub/models 的最小字段）。 */
export interface HostModelSpec { id: string; name: string; modelUrl: string; }

export interface HostTransform {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
}

export interface HostObjectToolsOptions {
    app: Application;
    camera: Entity;
    canvas: HTMLCanvasElement;
    getCollision: () => Collision | null;
    getObjects: () => SceneObjectManager | null;
    /** 左键确认放置（main.ts 调 session.spawnRoomObject）。 */
    onSpawn(model: HostModelSpec, pos: [number, number, number]): void;
    /** Gizmo 拖拽结束（main.ts 调 session.moveRoomObject）。 */
    onMove(id: string, transform: HostTransform): void;
    onHint(message: string): void;
    /** 进入会话时释放指针锁。 */
    requestUnlock(): void;
    /** 右键拖动转视角（接 input.frame.appendRotate）。 */
    onLook?(movementX: number, movementY: number): void;
}

export class HostObjectTools {
    /** 放置或编辑进行中（main.ts 据此 gate 常规输入）。 */
    active = false;
    /** Gizmo 手柄正被拖拽（main.ts 可读，语义同 editor.isGizmoDragging）。 */
    get dragging(): boolean { return this._gizmo.isDragging; }

    private _opts: HostObjectToolsOptions;
    private _state = new EditorState(() => []);
    private _placement: EditorPlacement;
    private _gizmo: EditorGizmo;
    private _editId: string | null = null;
    private _hud: HTMLElement;
    private _hudText: HTMLElement;
    private _mx = 0;
    private _my = 0;
    private _mouseValid = false;
    private _leftDown = false;
    private _rightDown = false;
    private _downX = 0;
    private _downY = 0;
    private _dragged = false;
    private _rayOrigin = new Vec3();
    private _rayDir = new Vec3();
    private _far = new Vec3();

    constructor(opts: HostObjectToolsOptions) {
        this._opts = opts;
        this._placement = new EditorPlacement(
            opts.app, this._state, opts.getCollision,
            (asset, pos) => this._onPlaced(asset, pos));
        this._gizmo = new EditorGizmo(opts.app, opts.camera, {
            onTransformStart: () => { /* 拖拽开始：仅本地预览 */ },
            onTransformMove: (id) => this._syncEntityToData(id),
            onTransformEnd: (id) => this._commitTransform(id),
        });
        this._hud = document.createElement('div');
        this._hud.id = 'wh-host-hud';
        this._hud.className = 'hidden';
        this._hudText = document.createElement('span');
        this._hudText.className = 'wh-host-hint';
        const done = document.createElement('button');
        done.className = 'wh-host-done';
        done.textContent = '完成';
        done.addEventListener('click', () => this.cancel());
        this._hud.append(this._hudText, done);
        (document.getElementById('ui-layer') ?? document.body).appendChild(this._hud);
        this._bindInput();
    }

    // ── 会话入口 ─────────────────────────────────────────────────────────

    /** 光标放置模式：预览跟随光标（射线吸附表面 / 滚轮调距），左键确认。 */
    beginPlace(model: HostModelSpec): void {
        this.cancel();
        this.active = true;
        this._opts.requestUnlock();
        const asset: EditorAsset = {
            id: model.id,
            name: model.name,
            category: 'host-model',
            objectType: 'model',
            marker: { kind: 'glb', url: model.modelUrl, scale: 1 }
        };
        this._placement.begin(asset);
        this._showHud(`放置「${model.name}」：光标瞄准 · 滚轮调距 · 右键拖动转视角 · 左键放置 · Esc 取消`);
    }

    /** 编辑模式：Gizmo 直接附着到指定动态物体（W 移动 / E 旋转，拖拽松手同步）。 */
    editObject(id: string): void {
        this.cancel();
        const mgr = this._opts.getObjects();
        const node = mgr?.getMarkerEntity(id);
        if (!mgr || !node) { this._opts.onHint('物体不存在或尚未加载完成'); return; }
        this.active = true;
        this._opts.requestUnlock();
        this._editId = id;
        this._gizmo.setMode('move');
        this._gizmo.attach(id, node);
        this._showHud('移动物体：W 移动 / E 旋转 · 拖拽手柄调整（松手同步给全员）· 右键拖动转视角 · Esc / 完成 结束');
    }

    /** 结束当前会话（取消放置 / 退出编辑）。 */
    cancel(): void {
        this._placement.cancel();
        if (this._editId) this._gizmo.detach();
        this._editId = null;
        this.active = false;
        this._hud.classList.add('hidden');
    }

    /** 主循环每帧调用：更新放置预览（仅会话激活时）。 */
    update(): void {
        if (!this.active) return;
        if (this._placement.active) {
            this._updateRay();
            this._placement.update(this._rayOrigin, this._rayDir);
        }
    }

    destroy(): void {
        this.cancel();
        this._gizmo.destroy();
        this._hud.remove();
    }

    // ── 内部 ─────────────────────────────────────────────────────────────

    private _showHud(text: string): void {
        this._hudText.textContent = text;
        this._hud.classList.remove('hidden');
    }

    /** 光标 → 世界射线（与 SceneEditor._updateRay 同源算法）。 */
    private _updateRay(): void {
        const mx = this._mouseValid ? this._mx : window.innerWidth / 2;
        const my = this._mouseValid ? this._my : window.innerHeight / 2;
        this._opts.camera.getWorldTransform().getTranslation(this._rayOrigin);
        this._opts.camera.camera!.screenToWorld(mx, my, 1000, this._far);
        this._rayDir.set(this._far.x - this._rayOrigin.x, this._far.y - this._rayOrigin.y, this._far.z - this._rayOrigin.z).normalize();
    }

    private _onPlaced(asset: EditorAsset, pos: [number, number, number]): void {
        // 会话在 placement 内部已 cancel；这里保持 active 由外层 HUD 收尾。
        this.cancel();
        this._opts.onSpawn(
            { id: asset.id, name: asset.name, modelUrl: (asset.marker as { url: string }).url },
            pos);
    }

    /** 拖拽中：把 marker 实体的实时变换写回 SceneObject 数据（纯本地预览）。 */
    private _syncEntityToData(id: string): void {
        const mgr = this._opts.getObjects();
        const node = mgr?.getMarkerEntity(id);
        if (!mgr || !node) return;
        const p = node.getPosition();
        const e = node.getEulerAngles();
        const s = node.getLocalScale();
        mgr.applyTransform(id, [p.x, p.y, p.z], [e.x, e.y, e.z], [s.x, s.y, s.z]);
    }

    /** 拖拽结束：上行完整变换（服务端广播，本人数据已在拖拽中同步）。 */
    private _commitTransform(id: string): void {
        const mgr = this._opts.getObjects();
        const obj = mgr?.get(id);
        if (!obj) return;
        this._opts.onMove(id, {
            position: [...obj.transform.position] as [number, number, number],
            rotation: [...obj.transform.rotation] as [number, number, number],
            scale: [...obj.transform.scale] as [number, number, number]
        });
    }

    private _bindInput(): void {
        const canvas = this._opts.canvas;
        const DRAG_THRESHOLD = 4;
        canvas.addEventListener('mousemove', (e) => {
            if (!this.active) return;
            this._mx = e.clientX;
            this._my = e.clientY;
            this._mouseValid = true;
            if (this._gizmo.isDragging) return;
            if (this._leftDown) {
                if (!this._dragged && Math.hypot(e.clientX - this._downX, e.clientY - this._downY) > DRAG_THRESHOLD) this._dragged = true;
                // 左拖保留给未来的相机控制（当前用右键），不做事。
            } else if (this._rightDown) {
                this._opts.onLook?.(e.movementX, e.movementY);
            }
        });
        canvas.addEventListener('mousedown', (e) => {
            if (!this.active) return;
            if (this._gizmo.isDragging) return;
            if (e.button === 0) { this._leftDown = true; this._downX = e.clientX; this._downY = e.clientY; this._dragged = false; }
            else if (e.button === 2) this._rightDown = true;
        });
        window.addEventListener('mouseup', (e) => {
            if (!this.active) return;
            if (e.button === 0) {
                // 左键单击（无拖拽、非 Gizmo 手柄）= 确认放置。
                if (this._leftDown && !this._dragged && !this._gizmo.isDragging && this._placement.active)
                    this._placement.commit();
                this._leftDown = false;
            } else if (e.button === 2) this._rightDown = false;
        });
        canvas.addEventListener('contextmenu', (e) => {
            if (this.active) e.preventDefault();
        });
        canvas.addEventListener('wheel', (e) => {
            if (!this.active || !this._placement.active) return;
            this._placement.onWheel(e.deltaY);
            e.preventDefault();
        }, { passive: false });
        window.addEventListener('keydown', (e) => {
            if (!this.active || this._gizmo.isDragging) return;
            switch (e.code) {
                case 'Escape':
                    this.cancel();
                    this._opts.onHint('已取消物体操作');
                    break;
                case 'KeyW':
                    if (this._editId) this._gizmo.setMode('move');
                    break;
                case 'KeyE':
                    if (this._editId) this._gizmo.setMode('rotate');
                    break;
            }
        });
    }
}
